/**
 * A page of repository markdown: its own contents list, then the rendered
 * article.
 *
 * `renderMarkdown` was already shared between the format spec page and the
 * Help guides, but everything either page did *with* the result was not: the
 * same nav, the same `depth === 2` filter, the same `dangerouslySetInnerHTML`
 * article, spelled out twice. A guide is not a spec, but a page of our
 * markdown is a page of our markdown, and there is only one of those.
 *
 * The class names say `prose`, not `spec`, for the same reason this component
 * exists: a restyle aimed at the format spec used to reach every Help guide
 * through a name that claimed otherwise.
 */

import { useMemo } from "react";

import { renderMarkdown } from "./markdown";

interface Props {
  /** The markdown source, as bundled by Vite's `?raw` import. */
  source: string;
  /** Names the contents nav — a page can hold more than one nav, and "Contents"
   * alone does not say contents *of what*. */
  tocLabel: string;
}

export function MarkdownPage({ source, tocLabel }: Props) {
  const { html, headings } = useMemo(() => renderMarkdown(source), [source]);
  // Only `##` sections. Deeper headings still carry ids, so links into them
  // resolve; listing every one of them here would be a wall rather than a map.
  const sections = headings.filter((h) => h.depth === 2);

  return (
    <>
      {sections.length > 0 && (
        <nav className="panel prose__toc" aria-label={tocLabel}>
          <h2>Contents</h2>
          <ul>
            {sections.map((h) => (
              <li key={h.id}>
                <a href={`#${h.id}`}>{h.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <article className="panel prose" dangerouslySetInnerHTML={{ __html: html }} />
    </>
  );
}
